import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { priceFor } from '@/lib/api';

const CartCtx = createContext(null);
const STORAGE_KEY = 'kanbr_cart_v2';

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch { return []; }
    });
    const [globalQuality, setGlobalQuality] = useState('prime');

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = (product, quantity = 1, quality) => {
        const q = quality || globalQuality;
        setItems((prev) => {
            const key = `${product.id}::${q}`;
            const existing = prev.find((i) => `${i.product.id}::${i.quality}` === key);
            if (existing) {
                return prev.map((i) => (`${i.product.id}::${i.quality}` === key ? { ...i, quantity: i.quantity + quantity } : i));
            }
            return [...prev, { product, quantity, quality: q }];
        });
    };

    const removeItem = (product_id, quality) => setItems((prev) => prev.filter((i) => !(i.product.id === product_id && i.quality === quality)));

    const updateQty = (product_id, quality, quantity) => {
        setItems((prev) => prev
            .map((i) => (i.product.id === product_id && i.quality === quality ? { ...i, quantity: Math.max(0, quantity) } : i))
            .filter((i) => i.quantity > 0));
    };

    const updateQuality = (product_id, oldQ, newQ) => setItems((prev) => prev.map((i) => (i.product.id === product_id && i.quality === oldQ ? { ...i, quality: newQ } : i)));

    const clearCart = () => setItems([]);

    const subtotal = useMemo(() =>
        items.reduce((s, i) => s + priceFor(i.product, i.quality) * i.quantity, 0), [items]);

    const totalCount = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

    return (
        <CartCtx.Provider value={{ items, addItem, removeItem, updateQty, updateQuality, clearCart, subtotal, totalCount, globalQuality, setGlobalQuality }}>
            {children}
        </CartCtx.Provider>
    );
}

export const useCart = () => {
    const ctx = useContext(CartCtx);
    if (!ctx) throw new Error('useCart must be inside CartProvider');
    return ctx;
};
